import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Eye,
  Loader2,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  useGetAssessmentItemExchangesPaged,
  useDeleteAssessmentItemExchange,
} from '../hooks/AssessmentItemExchange';
import { BaseAssessmentItemExchange } from '../dtos/AssessmentItemExchanges/index';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

export default function AssessmentItemExchanges() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BaseAssessmentItemExchange | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const searchTerm = searchParams.get('search') || '';

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const { data: response, isLoading } = useGetAssessmentItemExchangesPaged(page, pageSize, searchTerm || undefined);
  const deleteMutation = useDeleteAssessmentItemExchange();

  const pagedData = response?.isSuccess ? response.data : null;
  const items = pagedData?.items || [];
  const totalCount = pagedData?.totalCount || 0;
  const totalPages = pagedData?.totalPages || 1;

  const updateParams = (updates: { page?: number; size?: number; search?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    if (updates.page !== undefined) {
      if (updates.page === 1) newParams.delete('page');
      else newParams.set('page', updates.page.toString());
    }
    if (updates.size !== undefined) {
      if (updates.size === 10) newParams.delete('size');
      else newParams.set('size', updates.size.toString());
    }
    if (updates.search !== undefined) {
      if (updates.search === '') newParams.delete('search');
      else newParams.set('search', updates.search);
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  const handleSearch = () => updateParams({ search: searchInput });
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const clearSearch = () => {
    setSearchInput('');
    updateParams({ search: '' });
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    const res = await deleteMutation.mutateAsync(selectedItem.id);
    if (res.isSuccess) {
      setIsDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  const openViewDialog = (item: BaseAssessmentItemExchange) => {
    setSelectedItem(item);
    setIsViewDialogOpen(true);
  };

  const openDeleteDialog = (item: BaseAssessmentItemExchange) => {
    setSelectedItem(item);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Răspunsuri Întrebări</h1>
          <p className="text-muted-foreground mt-2">Vizualizați răspunsurile la întrebările din evaluări.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5" />
            Lista Răspunsuri
          </CardTitle>
          <CardDescription>{totalCount} răspunsuri în total</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Caută răspunsuri..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button onClick={handleSearch} variant="secondary">
              <Search className="h-4 w-4 mr-2" />
              Caută
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit răspunsuri.' : 'Nu există răspunsuri.'}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID Sesiune</TableHead>
                      <TableHead>ID Întrebare</TableHead>
                      <TableHead>Scor</TableHead>
                      <TableHead>Răspuns Marcat</TableHead>
                      <TableHead className="w-[100px]">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-xs">
                          {item.assessmentExchangeId.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {item.assessmentItemId.slice(0, 8)}...
                        </TableCell>
                        <TableCell>
                          {item.score !== undefined ? (
                            <Badge variant={item.score > 0 ? 'default' : 'secondary'}>
                              {item.score}
                            </Badge>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {item.answerMarked ? (
                            <span className="text-sm">{item.answerMarked.displayValue || item.answerMarked.submittedValue || '-'}</span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => openViewDialog(item)}
                              title="Vizualizează"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => openDeleteDialog(item)}
                              title="Șterge"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rânduri pe pagină:</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => updateParams({ size: parseInt(value, 10), page: 1 })}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Pagina {page} din {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page - 1 })}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page + 1 })}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Detalii Răspuns
            </DialogTitle>
            <DialogDescription>Informații despre răspunsul la întrebare.</DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Scor</p>
                  <p className="text-sm">{selectedItem.score ?? '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Opțiuni Răspuns</p>
                  <p className="text-sm">{selectedItem.answers?.length || 0} opțiuni</p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Răspuns Marcat</p>
                {selectedItem.answerMarked ? (
                  <div className="mt-1 p-2 bg-muted rounded text-sm">
                    {selectedItem.answerMarked.displayValue || selectedItem.answerMarked.submittedValue || JSON.stringify(selectedItem.answerMarked)}
                  </div>
                ) : (
                  <p className="text-sm">-</p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID Sesiune Evaluare</p>
                <p className="text-sm font-mono">{selectedItem.assessmentExchangeId}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">ID Întrebare</p>
                <p className="text-sm font-mono">{selectedItem.assessmentItemId}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmați ștergerea</AlertDialogTitle>
            <AlertDialogDescription>
              Sunteți sigur că doriți să ștergeți acest răspuns? Această acțiune nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedItem(null)}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Se șterge...
                </>
              ) : (
                'Șterge'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
